import { useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { api } from '../lib/api';

interface StreetMetadata {
  available: boolean;
  capture_date?: string;
  location?: { lat: number; lng: number };
  pano_id?: string;
  static_url?: string;
}

interface AnalysisResult {
  property_id: string;
  image_source: string;
  capture_date?: string;
  ai_analysis: {
    building_condition?: number;
    occupancy_indicators?: string[];
    signage_detected?: string[];
    parking_activity?: string;
    notable_observations?: string[];
    overall_assessment?: string;
  };
  analyzed_at: string;
}

function freshnessColor(date?: string): string {
  if (!date) return 'text-gray-400';
  const years = (Date.now() - new Date(date).getTime()) / (365.25 * 24 * 60 * 60 * 1000);
  if (years < 1) return 'text-success';
  if (years < 3) return 'text-warning';
  return 'text-red-600';
}

function freshnessLabel(date?: string): string {
  if (!date) return 'Unknown';
  const years = (Date.now() - new Date(date).getTime()) / (365.25 * 24 * 60 * 60 * 1000);
  if (years < 1) return 'Fresh (<1 yr)';
  if (years < 3) return 'Aging (1-3 yr)';
  return 'Stale (>3 yr)';
}

export default function StreetAnalysis() {
  const [address, setAddress] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [metadata, setMetadata] = useState<StreetMetadata | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [history, setHistory] = useState<AnalysisResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');

  const lookupStreet = useCallback(async () => {
    if (!address.trim()) return;
    setLoading(true);
    setError('');
    setMetadata(null);
    setAnalysis(null);
    try {
      const res = await api.getStreetMetadata(address);
      setMetadata(res);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch street view data');
    } finally {
      setLoading(false);
    }
  }, [address]);

  const runAnalysis = useCallback(async () => {
    if (!propertyId.trim() || !address.trim()) {
      setError('Enter a property ID and address first');
      return;
    }
    setAnalyzing(true);
    setError('');
    try {
      const res = await api.runStreetAnalysis(propertyId, address);
      setAnalysis(res.analysis);
      // Refresh history
      const historyRes = await api.getAnalysisHistory(propertyId);
      setHistory(historyRes.analyses || []);
    } catch (err: any) {
      setError(err.message || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  }, [propertyId, address]);

  const loadHistory = useCallback(async () => {
    if (!propertyId.trim()) return;
    try {
      const res = await api.getAnalysisHistory(propertyId);
      setHistory(res.analyses || []);
    } catch { /* ignore */ }
  }, [propertyId]);

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">Street-Level Analysis</h1>
          <p className="text-sm text-gray-500 mt-1">AI-powered property condition assessment using street imagery</p>
        </div>

        {/* Search controls */}
        <div className="bg-white rounded-lg border p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Property Address</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="e.g., 123 Industrial Way, Ontario, CA"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
                onKeyDown={(e) => e.key === 'Enter' && lookupStreet()}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Property ID (for analysis)</label>
              <input
                type="text"
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
                placeholder="UUID from Data Hub"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={lookupStreet}
              disabled={loading || !address.trim()}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50"
            >
              {loading ? 'Looking up...' : 'Lookup Street View'}
            </button>
            <button
              onClick={runAnalysis}
              disabled={analyzing || !address.trim() || !propertyId.trim()}
              className="px-4 py-2 bg-info text-white rounded-lg text-sm font-medium hover:bg-info/85 disabled:opacity-50"
            >
              {analyzing ? 'Analyzing...' : 'AI Analyze'}
            </button>
            {propertyId && (
              <button
                onClick={loadHistory}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                Load History
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
        )}

        {/* Street View Preview + Metadata */}
        {metadata && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg border overflow-hidden">
              {metadata.static_url ? (
                <img src={metadata.static_url} alt="Street View" className="w-full h-64 object-cover" />
              ) : (
                <div className="w-full h-64 bg-gray-100 flex items-center justify-center text-gray-400 text-sm">
                  {metadata.available ? 'Street View available' : 'No Street View imagery available'}
                </div>
              )}
            </div>
            <div className="bg-white rounded-lg border p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">Street View Metadata</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Available</span>
                  <span className={metadata.available ? 'text-success font-medium' : 'text-red-600'}>
                    {metadata.available ? 'Yes' : 'No'}
                  </span>
                </div>
                {metadata.capture_date && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Capture Date</span>
                    <span className={`font-medium ${freshnessColor(metadata.capture_date)}`}>
                      {metadata.capture_date} ({freshnessLabel(metadata.capture_date)})
                    </span>
                  </div>
                )}
                {metadata.location && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Coordinates</span>
                    <span className="text-gray-700 font-mono text-xs">
                      {metadata.location.lat.toFixed(6)}, {metadata.location.lng.toFixed(6)}
                    </span>
                  </div>
                )}
                {metadata.pano_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Panorama ID</span>
                    <span className="text-gray-700 font-mono text-xs">{metadata.pano_id.slice(0, 16)}...</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* AI Analysis Results */}
        {analysis && (
          <div className="bg-white rounded-lg border p-4 space-y-4">
            <h3 className="text-sm font-semibold text-gray-900">AI Analysis Results</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Building Condition</p>
                <p className="text-2xl font-bold text-gray-900">{analysis.ai_analysis.building_condition || '—'}<span className="text-sm text-gray-400">/10</span></p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Parking Activity</p>
                <p className="text-sm font-medium text-gray-900">{analysis.ai_analysis.parking_activity || '—'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Image Source</p>
                <p className="text-sm font-medium text-gray-900">{analysis.image_source}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Analyzed</p>
                <p className="text-sm font-medium text-gray-900">{new Date(analysis.analyzed_at).toLocaleDateString()}</p>
              </div>
            </div>

            {analysis.ai_analysis.occupancy_indicators && analysis.ai_analysis.occupancy_indicators.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Occupancy Indicators</p>
                <div className="flex flex-wrap gap-2">
                  {analysis.ai_analysis.occupancy_indicators.map((ind, i) => (
                    <span key={i} className="px-2 py-1 bg-info-wash text-info rounded text-xs">{ind}</span>
                  ))}
                </div>
              </div>
            )}

            {analysis.ai_analysis.signage_detected && analysis.ai_analysis.signage_detected.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Signage Detected</p>
                <div className="flex flex-wrap gap-2">
                  {analysis.ai_analysis.signage_detected.map((s, i) => (
                    <span key={i} className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">{s}</span>
                  ))}
                </div>
              </div>
            )}

            {analysis.ai_analysis.notable_observations && analysis.ai_analysis.notable_observations.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Notable Observations</p>
                <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                  {analysis.ai_analysis.notable_observations.map((obs, i) => (
                    <li key={i}>{obs}</li>
                  ))}
                </ul>
              </div>
            )}

            {analysis.ai_analysis.overall_assessment && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Overall Assessment</p>
                <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{analysis.ai_analysis.overall_assessment}</p>
              </div>
            )}
          </div>
        )}

        {/* Analysis History */}
        {history.length > 0 && (
          <div className="bg-white rounded-lg border p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">Analysis History</h3>
            <div className="space-y-2">
              {history.map((h, i) => (
                <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-500">{new Date(h.analyzed_at).toLocaleDateString()}</span>
                    <span className="text-sm text-gray-700">{h.image_source}</span>
                    {h.ai_analysis.building_condition && (
                      <span className="text-sm font-medium text-gray-900">Condition: {h.ai_analysis.building_condition}/10</span>
                    )}
                  </div>
                  <button
                    onClick={() => setAnalysis(h)}
                    className="text-xs text-primary-500 hover:text-primary-600"
                  >
                    View
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
